"use client";

import Link from "next/link";
import { ArrowRight, Mic } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { lpCopy, type LpAudience, type LpStep1Card } from "@/data/lpCopy";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  HOW_TO_USE_PATH,
  HOW_TO_USE_DETAIL_LABEL,
} from "@/data/evaluationGuide";

// ── アニメーション用データ ──
const waveHeights = [
  12, 28, 20, 36, 16, 40, 24, 32, 18, 28, 14, 36, 22, 30, 16,
];
const waveDelays = [
  0, 0.1, 0.2, 0.15, 0.3, 0.05, 0.25, 0.12, 0.22, 0.08, 0.18, 0.28, 0.02, 0.14,
  0.24,
];

// ── ビジュアルコンポーネント ──
function Step1Visual({ cards }: { cards: LpStep1Card[] }) {
  return (
    <div className="flex flex-col gap-3">
      {cards.map((c) => (
        <div
          key={c.id}
          className={`border rounded-xl px-5 py-4 flex items-center gap-3.5 transition-all ${
            c.active
              ? "border-primary shadow-[0_0_0_3px_rgba(233,89,98,0.12)] bg-primary/5"
              : "border-outline-variant bg-white"
          }`}
        >
          <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-sm font-extrabold shrink-0">
            {c.id}
          </div>
          <div>
            <div className="text-sm font-semibold">{c.text}</div>
            <div className="text-[11px] text-on-surface-variant mt-0.5">
              {c.cat}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Step2Visual() {
  return (
    <div className="text-center py-5">
      <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_0_12px_rgba(233,89,98,0.12),0_0_0_24px_rgba(233,89,98,0.06)]">
        <Mic className="w-8 h-8 text-white" />
      </div>
      <div className="flex items-center justify-center gap-[3px] h-12 mb-4">
        {waveHeights.map((h, i) => (
          <div
            key={i}
            className="w-1 bg-primary rounded-sm animate-[wave_1.2s_ease-in-out_infinite]"
            style={{
              height: `${h}px`,
              animationDelay: `${waveDelays[i]}s`,
            }}
          />
        ))}
      </div>
      <div className="text-2xl font-bold tabular-nums">1:42</div>
      <div className="text-[13px] text-on-surface-variant mt-1">回答中...</div>
    </div>
  );
}

function Step3Visual() {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(44,26,30,0.06)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-12 h-12 bg-primary/[0.06] rounded-bl-full" />
          <div className="text-[10px] font-bold uppercase tracking-[1.5px] text-on-surface-variant mb-2">
            総合スコア
          </div>
          <div className="flex items-end gap-1">
            <span className="text-[40px] font-extrabold text-primary leading-none">
              72
            </span>
            <span className="text-sm text-on-surface-variant font-semibold">
              / 100
            </span>
            <span className="bg-primary/10 text-primary font-extrabold text-lg px-3 py-1 rounded-lg ml-2">
              B
            </span>
          </div>
          <div className="text-[11px] font-semibold text-warning mt-1.5">
            合格圏内まであと少しです
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex-1 bg-success/[0.08] border border-success/15 rounded-[10px] px-3.5 py-3">
            <div className="flex items-center gap-1.5 text-xs font-bold text-success mb-1.5">
              ✓ 良かった点
            </div>
            <div className="text-[11px] text-on-surface-variant leading-relaxed">
              結論ファーストで話せており、技術的な背景も簡潔に伝えられています。
            </div>
          </div>
          <div className="flex-1 bg-warning/[0.08] border border-warning/15 rounded-[10px] px-3.5 py-3 relative overflow-hidden">
            <div className="flex items-center gap-1.5 text-xs font-bold text-warning mb-1.5">
              ✦ 改善ポイント
            </div>
            <div
              className="text-[11px] text-on-surface-variant leading-relaxed"
              style={{
                maskImage:
                  "linear-gradient(to bottom, #000 40%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, #000 40%, transparent 100%)",
              }}
            >
              「チームで解決した」の部分で、あなた自身の役割と定量的な成果を加えましょう。
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(44,26,30,0.06)]">
        <div className="text-[10px] font-bold uppercase tracking-[1.5px] text-on-surface-variant mb-2">
          詳細スコア
        </div>
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <span className="w-[72px] text-[11px] font-bold shrink-0">
              論理構造
            </span>
            <div className="flex-1 h-1.5 bg-[#f0eded] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: "75%" }}
              />
            </div>
            <span className="w-7 text-[11px] font-bold text-right text-primary">
              75
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="w-[72px] text-[11px] font-bold shrink-0">
              具体性
            </span>
            <div className="flex-1 h-1.5 bg-[#f0eded] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-warning"
                style={{ width: "68%" }}
              />
            </div>
            <span className="w-7 text-[11px] font-bold text-right text-warning">
              68
            </span>
          </div>
        </div>
        <div className="text-center mt-2 text-xs text-primary font-semibold">
          他3つの軸の詳細を見る →
        </div>
      </div>
    </div>
  );
}

// ── ステップデータ ──
function buildSteps(audience: LpAudience) {
  const { howItWorks } = lpCopy[audience];
  return [
    {
      number: "01",
      title: "練習したい質問を選ぶ",
      description: howItWorks.step1Description,
      visual: <Step1Visual cards={howItWorks.step1Cards} />,
    },
    {
      number: "02",
      title: "声で答える",
      description:
        "実際の面接と同じように、マイクで声を出して回答します。面接本番で緊張してしまう原因の多くは「声に出して練習する機会がないこと」にあります。MENRENなら、誰かに見られる心配もなく、何度でも納得ゆくまで繰り返し練習できます。",
      visual: <Step2Visual />,
    },
    {
      number: "03",
      title: "AIがフィードバック",
      description: howItWorks.step3Description,
      visual: <Step3Visual />,
    },
  ];
}

const TOTAL_STEPS = 3;

interface Props {
  audience: LpAudience;
}

// ── メインコンポーネント ──
export default function HowItWorksSection({ audience }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  // モバイル（md未満）ではスクロール連動の100vh表示をやめ、通常の縦スクロールで
  // 全ステップを全文表示する。スクロールジャックは小型端末で見出し・説明文・
  // ビジュアルが見切れる原因になるため、md以上でのみ有効化する。
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const steps = buildSteps(audience);

  useEffect(() => {
    // スクロール連動はPC（スティッキー表示）でのみ意味を持つ。
    if (!isDesktop) return;

    const handleScroll = () => {
      const section = sectionRef.current;
      if (!section) return;

      const { top, height } = section.getBoundingClientRect();
      const scrolledIn = Math.max(0, -top);
      const scrollableRange = height - window.innerHeight;

      if (scrollableRange <= 0) return;

      const progress = Math.min(1, scrolledIn / scrollableRange);
      const step = Math.min(
        TOTAL_STEPS - 1,
        Math.floor(progress * TOTAL_STEPS),
      );
      setActiveStep(step);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktop]);

  return (
    <section
      ref={sectionRef}
      className="relative bg-text"
      id="how-it-works"
      style={isDesktop ? { height: `${TOTAL_STEPS * 100}vh` } : undefined}
    >
      {/* PC: スティッキーで100vh中央寄せ / モバイル: 通常フロー（縦スクロール）*/}
      <div
        className={
          isDesktop
            ? "sticky top-0 h-screen flex flex-col justify-center overflow-hidden"
            : "py-16"
        }
      >
        <div className="max-w-[1100px] mx-auto px-5 w-full flex flex-col gap-8 md:gap-10">
          {/* ヘッダー */}
          <div className="text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
              3ステップで面接力を鍛える
            </h2>
            <p className="text-base md:text-xl text-white font-bold">
              思い立ったらすぐ練習！スマホでもPCでも予約なしですぐに始められます
            </p>
          </div>

          {/* ドットインジケーター（PCのスクロール連動表示でのみ表示する）*/}
          {isDesktop && (
            <div
              data-testid="how-it-works-indicators"
              className="flex justify-center gap-2"
            >
              {steps.map((_, index) => (
                <div
                  key={index}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: index === activeStep ? "24px" : "8px",
                    height: "8px",
                    backgroundColor:
                      index === activeStep
                        ? "var(--color-primary)"
                        : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          )}

          {/* ステップ */}
          {/* PC: absolute overlay で1つずつ表示 / モバイル: 全件を縦に並べて全文表示 */}
          <div
            className={
              isDesktop ? "relative min-h-[360px]" : "flex flex-col gap-12"
            }
          >
            {steps.map((step, index) => (
              <div
                key={step.number}
                data-testid="how-it-works-step"
                className={`flex flex-col md:grid md:grid-cols-2 md:gap-16 md:items-center ${
                  isDesktop
                    ? "absolute inset-0 transition-all duration-500 ease-in-out"
                    : ""
                }`}
                style={
                  isDesktop
                    ? {
                        opacity: index === activeStep ? 1 : 0,
                        transform: `translateY(${
                          index === activeStep ? 0 : 24
                        }px)`,
                        pointerEvents: index === activeStep ? "auto" : "none",
                      }
                    : undefined
                }
              >
                <div className="shrink-0">
                  <div className="text-3xl md:text-5xl font-black text-white leading-none mb-2 md:mb-3">
                    #{step.number}
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-white mb-2 md:mb-3">
                    {step.title}
                  </h3>
                  <p className="text-base text-white leading-relaxed mb-4 md:mb-0">
                    {step.description}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 md:p-8 overflow-hidden">
                  {step.visual}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              href={HOW_TO_USE_PATH}
              className="inline-flex items-center gap-1.5 font-bold text-white hover:text-primary transition-colors"
            >
              {HOW_TO_USE_DETAIL_LABEL}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
